import React, { Component } from 'react';
import classnames from 'classnames';
import { Menu, MenuProps, Divider, IconButton, List } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import cookie from 'js-cookie';
import _ from 'lodash';

// Contexts
import { withNavigation, withTranslate } from '../../../core/TenantProvider';
// Components - Atoms, Organisms,Containers
import APSelectItem from '../../atoms/APSelect/APSelectItem';
// Files in the same folder
import { MENU_ITEMS } from './menuItems';
import ListItemLink from './ListItemLink';
import APUserProfileManagementMenuStyle from './styles/APUserProfileManagementMenu.style';
// Types
import { RoleAccessFunction } from '../../../core/types/AffiliateDetails';
// Redux
import { ReducerState } from '../../../redux/modules';
import { actions as SessionActions } from '../../../redux/modules/session';
import { actions as SlidingRightPanelActions } from '../../../redux/modules/uistates';
// Utils
import { ClearDialingCodeInstance } from '../../../core/utils/GetDialingCode';
import { convertIntoAccountSwitchOptions } from '../../../core/utils/ConvertIntoAccountSwitchOptions/ConvertIntoAccountSwitchOptions';
// Constants
import { CD_ZA_LEGAL_ENTITY, LEGAL_ENTITY_CDH_COMPANY_MAPPING, SSO_CDH_COMPANY_KEY } from '../../../core/utils/Constants/Constants';

interface APUserProfileManagementMenuProps extends WithStyles<typeof APUserProfileManagementMenuStyle> {
    className?: string,
    clearSession: Function,
    translate: Function,
    affiliateDetails: any,
    toggleSlidingRightPanelStatus: Function,
    navigate: any,
    roleAccessFunction: RoleAccessFunction
}

interface APUserProfileManagementState {
    anchorEl: any,
    isActive: boolean,
    selectedAccount: string
}

interface ProfileManagementRoleAccessMap {
    bankDetails: boolean;
    profile: boolean;
}

const StyledMenu = ((props: MenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: "bottom",
            horizontal: "center"
        }}
        transformOrigin={{
            vertical: "top",
            horizontal: "center"
        }}
        {...props}
    />
));

class APUserProfileManagementMenu extends Component<APUserProfileManagementMenuProps, APUserProfileManagementState, {}> {

    constructor(props: any) {
        super(props);
        const { affiliateDetails } = this.props;

        this.state = {
            anchorEl: null,
            isActive: false,
            selectedAccount: affiliateDetails?.affiliate?.SF_ID
        };
        this.onAccountChange = this.onAccountChange.bind(this);
    }

    onAccountChange = (option: any) => {
        if (this.state.selectedAccount !== option.value) {
            const referringCdhCompany = LEGAL_ENTITY_CDH_COMPANY_MAPPING[option?.legalEntity];

            sessionStorage.setItem('affiliate-id', option.value)
            sessionStorage.setItem('affiliate-number', option.affiliateNumber);

            cookie.set('affiliate-number', option.affiliateNumber, {
                path: '/'
            });
            cookie.set(SSO_CDH_COMPANY_KEY, referringCdhCompany, {
                path: '/'
            });

            this.setState({ selectedAccount: option.value }, () => {
                setTimeout(() => {
                    window.location.reload();
                }, 300);
            });
        };
    }

    showUserProfilePopper = (event: React.MouseEvent<HTMLElement>) => {
        this.setState({ anchorEl: event.currentTarget, isActive: true });
    };

    closeUserProfilePopper = (event: object, anchorEl?: any, isActive?: boolean) => {
        this.setState({ anchorEl: null, isActive: false });
    };

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any): void {
        const {
            affiliateDetails,
        } = this.props;

        const {
            affiliateDetails: prevAffiliateDetails,
        } = prevProps || {};

        if (!(_.isEqual(prevAffiliateDetails, affiliateDetails))) {
            this.setState({ selectedAccount: affiliateDetails?.affiliate?.SF_ID });
        }
    }

    render() {
        const { classes, translate, affiliateDetails, roleAccessFunction: { VIEW_BANK_DETAILS, VIEW_SETTINGS } } = this.props;
        const roleAccessMap: ProfileManagementRoleAccessMap = {
            bankDetails: VIEW_BANK_DETAILS,
            profile: VIEW_SETTINGS
        };
        const { company_name, legal_entity, affiliate_Number, SF_ID } = affiliateDetails?.affiliate || {};
        const { anchorEl, isActive } = this.state;
        const open = Boolean(anchorEl);
        const id = open ? 'APUserProfileCustomized-Menu' : '';

        const showAccountSwitchSection: boolean = Boolean(affiliateDetails?.affiliateRelatedRecords);
        const accountSwitchOptions = showAccountSwitchSection ? {
            options: [
                {
                    label: company_name,
                    icon: legal_entity === CD_ZA_LEGAL_ENTITY ? 'countries/south-africa' : 'countries/united-kingdom',
                    affiliateNumber: affiliate_Number,
                    legalEntity: legal_entity,
                    value: SF_ID
                },
                ...convertIntoAccountSwitchOptions(affiliateDetails?.affiliateRelatedRecords)
            ]
        } : {};

        return (
            <div>
                <IconButton
                    data-testid="showPopper"
                    className={classes.profileManagementIconButton}
                    onClick={this.showUserProfilePopper}
                    disableRipple
                    size="large">
                    <div>
                        <div className={classnames(isActive ? classes.profileManagementActiveIcon :
                            classes.profileManagementInActiveIcon, classes.profileManagementIcon)}>
                            <span>
                                {(affiliateDetails && affiliateDetails.affiliate) ?
                                    (`${(affiliateDetails?.affiliate?.user_first_name)?.charAt(0)?.toUpperCase()} 
                                        ${(affiliateDetails?.affiliate?.user_last_name)?.charAt(0)?.toUpperCase()}`) : ''}
                            </span>
                        </div>
                    </div>
                </IconButton>
                <div>
                    <StyledMenu
                        id={id}
                        anchorEl={this.state.anchorEl}
                        keepMounted
                        open={open}
                        className={classes.profileManagementContainer}
                        onClose={this.closeUserProfilePopper}
                    >
                        <div className={classes.profileDetailsMenuContainer}>
                            <div className={classes.profileManagementUserInfo}>
                                <h6>{`${affiliateDetails?.affiliate?.user_first_name} ${affiliateDetails?.affiliate?.user_last_name}`}</h6>
                                <h6>{translate('profile.management.userinfo.affiliate',
                                    {
                                        affiliate_id: affiliateDetails?.affiliate?.affiliate_Number
                                    })}
                                </h6>
                                <p>{affiliateDetails?.affiliate?.trading_name ? affiliateDetails.affiliate.trading_name : ''}</p>
                            </div>
                            <Divider />
                            <div className={classes.profileManagementMenuWrapper}>
                                <List aria-label="Profile Navigation">
                                    {MENU_ITEMS.map((item) => {
                                        const hasAccess = roleAccessMap[item.id] ?? true;
                                        const isBankDetailsForGroupAffiliate = 
                                            affiliateDetails?.affiliate?.pay_to_group_affiliate && 
                                            item.id === 'bankDetails';

                                        if (!hasAccess || isBankDetailsForGroupAffiliate) {
                                            return null;
                                        }

                                        return (
                                            <span key={item.id} onClick={this.closeUserProfilePopper}>
                                                <ListItemLink
                                                    to={item.route}
                                                    primary={translate(item.label)}
                                                    icon={item.icon}
                                                    handleClick={this.handleClick.bind(this, item.id)}
                                                />
                                                <Divider />
                                            </span>
                                        );
                                    })}
                                </List>
                            </div>
                        </div>
                        {
                            showAccountSwitchSection && (<div className={classes.switchAccountContainer}>
                                <h6>{translate('profile.management.accountswitch.title')}</h6>
                                <div className={classes.accountOptionsContainer}>
                                    {accountSwitchOptions?.options?.map((option) => (
                                        <APSelectItem
                                            type={'accountSelect'}
                                            className={classes.optionItem}
                                            {...option}
                                            aria-selected={this.state.selectedAccount === option.value}
                                            key={`${option.label}-${option.value}`}
                                            onClick={() => this.onAccountChange(option)}
                                        />
                                    ))}
                                </div>
                            </div>)
                        }

                    </StyledMenu>
                </div>
            </div>
        );
    }

    private handleClick(id: string) {
        const {
            clearSession,
            navigate
        } = this.props;

        switch (id) {
            case 'profile':
                navigate('/settings');
                break;
            case 'bankDetails':
                this.props.toggleSlidingRightPanelStatus(true, id);
                break;
            case 'logout':
                ClearDialingCodeInstance();
                clearSession(navigate);
                break;
        }
    }
}

const mapStateToProps = (store: ReducerState) => ({
    affiliateDetails: store.user.affiliateDetails,
    roleAccessFunction: store.user.roleAccessFunction
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    clearSession: SessionActions.clearSession,
    toggleSlidingRightPanelStatus: SlidingRightPanelActions.toggleSlidingRightPanelStatus
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withNavigation(withTranslate(withStyles(APUserProfileManagementMenuStyle)(APUserProfileManagementMenu))));
